import Constants from '@/helpers/Constants'
import store from '@/store'
import AuthBroker from '@/brokers/AuthBroker'

/**
 * Manages the client-side auth session for the DB-backed auth system.
 *
 * The opaque session token is persisted in localStorage (so it survives
 * reloads) and mirrored into Vuex for in-app access. Static methods are used so
 * the manager can be referenced from anywhere (e.g. AxiosManager) without
 * instance plumbing.
 */
export default class AuthManager {

  /**
   * Store the session in Vuex + localStorage.
   */
  private static setSession(token: string, username: string, permissions: string[],
                            mustChangePassword: boolean) {
    localStorage.setItem(Constants.LOCAL_STORAGE_IDS.ACCESS_TOKEN, token)
    store.dispatch('setAccessToken', token)
    store.dispatch('setUsername', username)
    store.dispatch('setPermissions', permissions || [])
    store.dispatch('setMustChangePassword', mustChangePassword)
    store.dispatch('setLoggedIn', true)
  }

  /**
   * Clear the session from Vuex + localStorage.
   */
  public static clearSession() {
    localStorage.removeItem(Constants.LOCAL_STORAGE_IDS.ACCESS_TOKEN)
    store.dispatch('logOut')
  }

  /**
   * Log in with username/password. Returns true on success.
   */
  public static async login(username: string, password: string): Promise<boolean> {
    const payload = await AuthBroker.login(username, password)
    if (!payload || !payload.token) {
      return false
    }
    this.setSession(payload.token, payload.username, payload.permissions,
                    payload.must_change_password)
    return true
  }

  /**
   * Log out: revoke the server session and clear local state.
   */
  public static async logout(): Promise<void> {
    await AuthBroker.logout()
    this.clearSession()
  }

  /**
   * Restore a session from a persisted token (on app start) by calling /me.
   * Returns true if a valid session was restored.
   */
  public static async restore(): Promise<boolean> {
    const token = localStorage.getItem(Constants.LOCAL_STORAGE_IDS.ACCESS_TOKEN)
    if (!token) {
      return false
    }
    store.dispatch('setAccessToken', token)
    try {
      const me = await AuthBroker.me()
      this.setSession(token, me.username, me.permissions, me.must_change_password)
      return true
    } catch (error) {
      this.clearSession()
      return false
    }
  }

  public static getToken(): string {
    return store.getters.getAccessToken ||
      localStorage.getItem(Constants.LOCAL_STORAGE_IDS.ACCESS_TOKEN) || ''
  }

  public static isLoggedIn(): boolean {
    return store.getters.isLoggedIn
  }

  public static mustChangePassword(): boolean {
    return store.getters.getMustChangePassword
  }
}
